import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import Picture from '../components/Picture';
import AdminNav from '../components/AdminNav';
import logo from '../assets/images/logoFree.webp';
import logoFallback from '../assets/images/logoFree.png';
import '../styles/AdminLogsPage.css';
import '../styles/AdminProductsPage.css';
import { clearAdminKey, getAdminKey } from '../utils/adminAuth';
import { ProductCard } from 'maf-react-components';
import { PlayIcon } from '@phosphor-icons/react';

const PRODUCTS_ENDPOINT = '/.netlify/functions/products-admin';
const UPLOAD_ENDPOINT = '/.netlify/functions/upload-product-media';
const STATUS_OPTIONS = [
    { value: 'draft', label: 'Brouillon' },
    { value: 'published', label: 'Publié' },
    { value: 'archived', label: 'Archivé' }
];
const CATEGORY_OPTIONS = [
    { value: 'tapis', label: 'Tapis' },
    { value: 'cartes', label: 'Cartes' }
];
const CATEGORY_FILTERS = [
    { value: 'all', label: 'Tous' },
    ...CATEGORY_OPTIONS
];

const getDefaultForm = (category = 'tapis') => ({
    id: null,
    slug: '',
    title: '',
    description: '',
    price: '',
    currency: 'EUR',
    is_sold: false,
    buy_link: '',
    status: 'draft',
    category,
    width_cm: '',
    height_cm: '',
    updated_by: '',
    options_text: '',
    media: []
});

const buildHeaders = (adminKey) => {
    const headers = { 'Content-Type': 'application/json' };
    if (adminKey) {
        headers['x-admin-key'] = adminKey;
    }
    return headers;
};

const NETLIFY_DEV_HINT =
    'Fonctions Netlify indisponibles. Lance `netlify dev` (port 8888) pour utiliser l admin produits en local.';

const parseJsonResponse = async (response, fallbackMessage) => {
    const contentType = response.headers.get('content-type') || '';
    const raw = await response.text();
    const isJson = contentType.includes('application/json');

    if (!response.ok) {
        if (isJson) {
            try {
                const payload = JSON.parse(raw);
                const message = payload?.error || payload?.message;
                throw new Error(message || fallbackMessage);
            } catch {
                throw new Error(fallbackMessage);
            }
        }
        throw new Error(raw || fallbackMessage);
    }

    if (!isJson) {
        if (raw.trim().startsWith('<!DOCTYPE') || raw.trim().startsWith('<html')) {
            throw new Error(NETLIFY_DEV_HINT);
        }
        throw new Error(fallbackMessage);
    }

    try {
        return JSON.parse(raw);
    } catch {
        throw new Error(fallbackMessage);
    }
};

const slugify = (value) =>
    String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9-_]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-+|-+$/g, '');

const readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = typeof reader.result === 'string' ? reader.result : '';
            const base64 = result.includes(',') ? result.split(',')[1] : result;
            resolve(base64);
        };
        reader.onerror = () => reject(new Error('Lecture fichier impossible'));
        reader.readAsDataURL(file);
    });

const IMAGE_MAX_WIDTH = 1600;
const IMAGE_WEBP_QUALITY = 0.82;
let ffmpegSingleton = null;

const fileExt = (name) => {
    const parts = String(name || '').split('.');
    return parts.length > 1 ? parts.pop().toLowerCase() : '';
};

const renameWithExt = (name, ext) => {
    const raw = String(name || 'upload');
    const base = raw.includes('.') ? raw.slice(0, raw.lastIndexOf('.')) : raw;
    return `${base}.${ext}`;
};

const optimizeImageFile = (file) =>
    new Promise((resolve) => {
        const sourceUrl = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            const ratio = Math.min(1, IMAGE_MAX_WIDTH / (img.width || 1));
            const width = Math.max(1, Math.round(img.width * ratio));
            const height = Math.max(1, Math.round(img.height * ratio));
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                URL.revokeObjectURL(sourceUrl);
                resolve(file);
                return;
            }
            ctx.drawImage(img, 0, 0, width, height);
            canvas.toBlob(
                (blob) => {
                    URL.revokeObjectURL(sourceUrl);
                    if (!blob) {
                        resolve(file);
                        return;
                    }
                    resolve(
                        new File([blob], renameWithExt(file.name, 'webp'), {
                            type: 'image/webp'
                        })
                    );
                },
                'image/webp',
                IMAGE_WEBP_QUALITY
            );
        };
        img.onerror = () => {
            URL.revokeObjectURL(sourceUrl);
            resolve(file);
        };
        img.src = sourceUrl;
    });

const getFfmpeg = async () => {
    if (ffmpegSingleton) {
        return ffmpegSingleton;
    }
    const mod = await import('@ffmpeg/ffmpeg');
    const { createFFmpeg, fetchFile } = mod;
    const ffmpeg = createFFmpeg({
        log: false,
        corePath: 'https://unpkg.com/@ffmpeg/core@0.11.0/dist/ffmpeg-core.js'
    });
    await ffmpeg.load();
    ffmpegSingleton = { ffmpeg, fetchFile };
    return ffmpegSingleton;
};

const optimizeVideoFile = async (file) => {
    try {
        const { ffmpeg, fetchFile } = await getFfmpeg();
        const inputExt = fileExt(file.name) || 'mov';
        const inputName = `in.${inputExt}`;
        const outputName = 'out.mp4';
        ffmpeg.FS('writeFile', inputName, await fetchFile(file));
        await ffmpeg.run(
            '-i',
            inputName,
            '-vf',
            'scale=min(1280\\,iw):-2',
            '-c:v',
            'libx264',
            '-preset',
            'veryfast',
            '-crf',
            '30',
            '-c:a',
            'aac',
            '-b:a',
            '96k',
            '-movflags',
            '+faststart',
            '-pix_fmt',
            'yuv420p',
            outputName
        );
        const out = ffmpeg.FS('readFile', outputName);
        try {
            ffmpeg.FS('unlink', inputName);
            ffmpeg.FS('unlink', outputName);
        } catch {
            // noop
        }
        return new File([out.buffer], renameWithExt(file.name, 'mp4'), {
            type: 'video/mp4'
        });
    } catch {
        return file;
    }
};

const optimizeMediaFile = async (file) => {
    if (!file || !file.type) {
        return file;
    }
    if (file.type.startsWith('image/')) {
        return optimizeImageFile(file);
    }
    if (file.type.startsWith('video/')) {
        return optimizeVideoFile(file);
    }
    return file;
};

const getPreviewPrice = (value, currency) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
        return 'Prix non defini';
    }
    try {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: currency || 'EUR'
        }).format(numeric);
    } catch {
        return `${numeric} ${currency || 'EUR'}`;
    }
};

const getMediaType = (url) => {
    const normalized = String(url || '').toLowerCase();
    if (!normalized) {
        return 'unknown';
    }
    const decoded = (() => {
        try {
            return decodeURIComponent(normalized);
        } catch {
            return normalized;
        }
    })();
    if (
        decoded.includes('.mp4') ||
        decoded.includes('.mov') ||
        decoded.includes('.webm') ||
        decoded.includes('.m4v')
    ) {
        return 'video';
    }
    return 'image';
};

const splitPreviewMedia = (urls) => {
    const source = Array.isArray(urls) ? urls.filter(Boolean) : [];
    const mediaItems = source.map((url, index) => ({
        url,
        type: getMediaType(url),
        label: `Media ${index + 1}`
    }));
    return mediaItems;
};

const normalizeMediaUrlForPage = (value) => {
    let normalized = String(value || '').trim();
    if (!normalized || typeof window === 'undefined') {
        return normalized;
    }
    if (
        normalized.includes('/product-media/') &&
        !normalized.includes('/.netlify/functions/product-media/')
    ) {
        normalized = normalized.replace(
            '/product-media/',
            '/.netlify/functions/product-media/'
        );
    }
    if (
        window.location.protocol === 'https:' &&
        normalized.startsWith('http://')
    ) {
        try {
            const parsed = new URL(normalized);
            const currentHost = window.location.host;
            if (parsed.host === currentHost) {
                parsed.protocol = 'https:';
                return parsed.toString();
            }
        } catch {
            return normalized;
        }
    }
    return normalized;
};

function AdminProductsPage() {
    const [items, setItems] = useState([]);
    const [status, setStatus] = useState('idle');
    const [feedback, setFeedback] = useState(null);
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [form, setForm] = useState(getDefaultForm('tapis'));
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [showFilters, setShowFilters] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isOptimizing, setIsOptimizing] = useState(false);
    const [manualMediaUrl, setManualMediaUrl] = useState('');
    const [previewMediaIndex, setPreviewMediaIndex] = useState(0);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const adminKey = getAdminKey();
    const isEdit =
        form.id !== null &&
        form.id !== undefined &&
        form.id !== '' &&
        Number.isFinite(Number(form.id));

    const redirectToLogin = useCallback(() => {
        clearAdminKey();
        navigate('/admin', {
            replace: true,
            state: { from: location }
        });
    }, [location, navigate]);

    const fetchProducts = useCallback(async () => {
        setStatus('loading');
        setFeedback(null);

        try {
            const params = new URLSearchParams();
            params.set('limit', '200');
            if (statusFilter && statusFilter !== 'all') {
                params.set('status', statusFilter);
            }
            const trimmedSearch = search.trim();
            if (trimmedSearch) {
                params.set('search', trimmedSearch);
            }
            const response = await fetch(
                `${PRODUCTS_ENDPOINT}?${params.toString()}`,
                { headers: buildHeaders(adminKey) }
            );

            if (response.status === 401) {
                redirectToLogin();
                return;
            }
            if (!response.ok) {
                throw new Error('Impossible de charger les produits.');
            }
            const payload = await parseJsonResponse(
                response,
                'Impossible de charger les produits.'
            );
            setItems(Array.isArray(payload.items) ? payload.items : []);
            setStatus('ready');
        } catch (error) {
            setStatus('error');
            setFeedback({ type: 'error', text: error.message });
        }
    }, [adminKey, redirectToLogin, search, statusFilter]);

    useEffect(() => {
        if (adminKey) {
            fetchProducts();
        }
    }, [adminKey, fetchProducts]);

    const filteredItems = useMemo(() => {
        if (categoryFilter === 'all') {
            return items;
        }
        return items.filter((item) => (item.category || 'tapis') === categoryFilter);
    }, [categoryFilter, items]);

    const previewMediaItems = useMemo(
        () => splitPreviewMedia(form.media),
        [form.media]
    );
    const safePreviewIndex = previewMediaItems.length
        ? ((previewMediaIndex % previewMediaItems.length) +
              previewMediaItems.length) %
          previewMediaItems.length
        : 0;
    const previewMedia = previewMediaItems[safePreviewIndex] || null;

    const stats = useMemo(() => {
        return items.reduce(
            (acc, item) => {
                const currentStatus = item.status || 'draft';
                const category = item.category || 'tapis';
                acc.total += 1;
                acc[category] = (acc[category] || 0) + 1;
                if (currentStatus === 'published') {
                    acc.published += 1;
                } else if (currentStatus === 'archived') {
                    acc.archived += 1;
                } else {
                    acc.draft += 1;
                }
                return acc;
            },
            { total: 0, draft: 0, published: 0, archived: 0, tapis: 0, cartes: 0 }
        );
    }, [items]);

    const resetForm = () => {
        const category = categoryFilter === 'all' ? 'tapis' : categoryFilter;
        setForm(getDefaultForm(category));
        setManualMediaUrl('');
    };

    const handleEdit = (item) => {
        setForm({
            id: item.id,
            slug: item.slug || '',
            title: item.title || '',
            description: item.description || '',
            price: item.price ?? '',
            currency: item.currency || 'EUR',
            is_sold: Boolean(item.is_sold),
            buy_link: item.buy_link || '',
            status: item.status || 'draft',
            category: item.category || 'tapis',
            width_cm: item.width_cm ?? '',
            height_cm: item.height_cm ?? '',
            updated_by: item.updated_by || '',
            options_text: Array.isArray(item.options) ? item.options.join('\n') : '',
            media: Array.isArray(item.media)
                ? item.media.map((entry) =>
                      typeof entry === 'string' ? entry : entry?.url || ''
                  )
                : []
        });
        setManualMediaUrl('');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    useEffect(() => {
        if (isEdit || categoryFilter === 'all') {
            return;
        }
        setForm((prev) => ({ ...prev, category: categoryFilter }));
    }, [categoryFilter, isEdit]);

    const handleInput = (key, value) => {
        setForm((prev) => ({
            ...prev,
            [key]: value
        }));
    };

    const handleSave = async (event) => {
        event.preventDefault();
        setIsSaving(true);
        setFeedback(null);

        const normalizedTitle = form.title.trim();
        const normalizedSlug = (form.slug || slugify(normalizedTitle)).trim();
        if (!normalizedTitle || !normalizedSlug) {
            setFeedback({
                type: 'error',
                text: 'Le titre et le slug sont obligatoires.'
            });
            setIsSaving(false);
            return;
        }

        const payload = {
            id: form.id,
            slug: normalizedSlug,
            title: normalizedTitle,
            description: form.description.trim(),
            price: form.price === '' ? null : Number(form.price),
            currency: (form.currency || 'EUR').trim() || 'EUR',
            is_sold: Boolean(form.is_sold),
            buy_link: form.buy_link.trim(),
            status: form.status || 'draft',
            category: form.category || 'tapis',
            options: form.options_text
                .split('\n')
                .map((item) => item.trim())
                .filter(Boolean),
            media: form.media.filter(Boolean),
            width_cm: form.width_cm === '' ? null : Number(form.width_cm),
            height_cm: form.height_cm === '' ? null : Number(form.height_cm),
            updated_by: form.updated_by.trim()
        };

        try {
            const response = await fetch(PRODUCTS_ENDPOINT, {
                method: isEdit ? 'PUT' : 'POST',
                headers: buildHeaders(adminKey),
                body: JSON.stringify(payload)
            });

            if (response.status === 401) {
                redirectToLogin();
                return;
            }
            if (!response.ok) {
                throw new Error('Enregistrement impossible.');
            }
            await parseJsonResponse(response, 'Enregistrement impossible.');

            await fetchProducts();
            resetForm();
            setFeedback({
                type: 'success',
                text: isEdit ? 'Produit mis a jour.' : 'Produit cree.'
            });
        } catch (error) {
            setFeedback({ type: 'error', text: error.message });
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id) => {
        setFeedback(null);
        setIsDeleting(true);
        try {
            const response = await fetch(`${PRODUCTS_ENDPOINT}?id=${id}`, {
                method: 'DELETE',
                headers: buildHeaders(adminKey)
            });

            if (response.status === 401) {
                redirectToLogin();
                return;
            }
            if (!response.ok) {
                throw new Error('Suppression impossible.');
            }
            await parseJsonResponse(response, 'Suppression impossible.');

            await fetchProducts();
            if (Number(form.id) === Number(id)) {
                resetForm();
            }
            setFeedback({ type: 'success', text: 'Produit supprime.' });
        } catch (error) {
            setFeedback({ type: 'error', text: error.message });
        } finally {
            setIsDeleting(false);
            setDeleteTarget(null);
        }
    };

    const pushMediaUrl = (url) => {
        const normalized = normalizeMediaUrlForPage(url);
        if (!normalized) {
            return;
        }
        setForm((prev) => ({
            ...prev,
            media: prev.media.includes(normalized)
                ? prev.media
                : [...prev.media, normalized]
        }));
    };

    const handleUploadFiles = async (event) => {
        event.preventDefault();
        event.stopPropagation();
        const input = event.currentTarget;
        const files = Array.from(input.files || []);
        if (!files.length) {
            return;
        }

        setIsUploading(true);
        setIsOptimizing(true);
        setFeedback(null);

        try {
            for (const file of files) {
                const optimized = await optimizeMediaFile(file);
                const data = await readFileAsBase64(optimized);
                const response = await fetch(UPLOAD_ENDPOINT, {
                    method: 'POST',
                    headers: buildHeaders(adminKey),
                    body: JSON.stringify({
                        name: optimized.name,
                        type: optimized.type || file.type,
                        data
                    })
                });

                if (response.status === 401) {
                    redirectToLogin();
                    return;
                }
                if (!response.ok) {
                    throw new Error(`Upload echoue pour ${file.name}`);
                }
                const payload = await parseJsonResponse(
                    response,
                    `Upload echoue pour ${file.name}`
                );
                pushMediaUrl(payload.mediaUrl);
            }
            setFeedback({
                type: 'success',
                text: 'Media optimise et ajoute au formulaire.'
            });
        } catch (error) {
            setFeedback({ type: 'error', text: error.message });
        } finally {
            setIsOptimizing(false);
            setIsUploading(false);
            input.value = '';
        }
    };

    const handleAddManualMedia = () => {
        pushMediaUrl(manualMediaUrl);
        setManualMediaUrl('');
    };

    const handleManualMediaKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleAddManualMedia();
        }
    };

    const handleRemoveMedia = (url) => {
        setForm((prev) => ({
            ...prev,
            media: prev.media.filter((entry) => entry !== url)
        }));
    };

    const handleMoveMedia = (index, direction) => {
        setForm((prev) => {
            const next = [...(prev.media || [])];
            const target = index + direction;
            if (target < 0 || target >= next.length) {
                return prev;
            }
            const temp = next[index];
            next[index] = next[target];
            next[target] = temp;
            return { ...prev, media: next };
        });
    };

    const handleLogout = () => {
        clearAdminKey();
        navigate('/admin', { replace: true });
    };

    if (!adminKey) {
        return (
            <Navigate
                to='/admin'
                replace
                state={{
                    from: location
                }}
            />
        );
    }

    return (
        <div className='page admin-analytics admin-products'>
            <div className='admin-nav-shell'>
                <AdminNav />
            </div>
            <main>
                <section className='hero admin-hero'>
                    <div className='container admin-hero-inner'>
                        <div className='admin-hero-copy'>
                            <div className='admin-brand'>
                                <Picture
                                    webpSrc={logo}
                                    fallbackSrc={logoFallback}
                                    alt='MAF Industries'
                                    className='admin-brand-logo'
                                    imgClassName='admin-brand-logo-img'
                                />
                                <div>
                                    <span className='admin-brand-title'>
                                        MAF Industries
                                    </span>
                                    <span className='admin-brand-subtitle'>
                                        Produits
                                    </span>
                                </div>
                            </div>
                            <h1>Gere tes produits en direct.</h1>
                            <p className='lead'>
                                Gere tapis et cartes 3D depuis un seul panel, avec
                                upload media inclus.
                            </p>
                            {status === 'loading' && (
                                <p className='admin-alert'>Chargement...</p>
                            )}
                            {feedback && (
                                <p
                                    className={`admin-alert ${
                                        feedback.type === 'error'
                                            ? 'admin-products-error'
                                            : 'admin-products-success'
                                    }`}
                                >
                                    {feedback.text}
                                </p>
                            )}
                        </div>
                        <div className='admin-hero-panel admin-products-stats'>
                            <div>
                                <p className='eyebrow'>Total</p>
                                <h2>{stats.total}</h2>
                            </div>
                            <div>
                                <p className='eyebrow'>Tapis</p>
                                <h2>{stats.tapis}</h2>
                            </div>
                            <div>
                                <p className='eyebrow'>Cartes</p>
                                <h2>{stats.cartes}</h2>
                            </div>
                            <div>
                                <p className='eyebrow'>Publies</p>
                                <h2>{stats.published}</h2>
                            </div>
                            <div>
                                <p className='eyebrow'>Brouillons</p>
                                <h2>{stats.draft}</h2>
                            </div>
                            <div>
                                <p className='eyebrow'>Archives</p>
                                <h2>{stats.archived}</h2>
                            </div>
                            <button
                                type='button'
                                className='button button-outline'
                                onClick={handleLogout}
                            >
                                Deconnexion
                            </button>
                        </div>
                    </div>
                </section>

                <section className='section'>
                    <div className='container admin-dashboard-grid admin-products-grid'>
                        <article className='admin-card admin-products-form-card'>
                            <div className='admin-card-header'>
                                <div>
                                    <p className='eyebrow'>
                                        {isEdit ? 'Edition' : 'Nouveau'}
                                    </p>
                                    <h2>
                                        {isEdit
                                            ? 'Modifier le produit'
                                            : 'Ajouter un produit'}
                                    </h2>
                                    <p className='admin-products-help'>
                                        {isEdit
                                            ? 'Tu modifies un produit existant. Clique sur "Reinitialiser" pour revenir en creation.'
                                            : 'Tu es en mode creation. Remplis le formulaire puis clique sur "Creer".'}
                                    </p>
                                </div>
                            </div>

                            <form
                                className='admin-products-form'
                                onSubmit={handleSave}
                            >
                                <label>
                                    Titre
                                    <input
                                        type='text'
                                        value={form.title}
                                        onChange={(event) =>
                                            handleInput('title', event.target.value)
                                        }
                                        required
                                    />
                                </label>
                                <label>
                                    Slug
                                    <input
                                        type='text'
                                        value={form.slug}
                                        onChange={(event) =>
                                            handleInput(
                                                'slug',
                                                slugify(event.target.value)
                                            )
                                        }
                                        placeholder='auto depuis le titre'
                                    />
                                </label>
                                <label className='admin-products-span-2'>
                                    Description
                                    <textarea
                                        rows={4}
                                        value={form.description}
                                        onChange={(event) =>
                                            handleInput(
                                                'description',
                                                event.target.value
                                            )
                                        }
                                    />
                                </label>
                                <label>
                                    Prix
                                    <input
                                        type='number'
                                        step='0.01'
                                        min='0'
                                        value={form.price}
                                        onChange={(event) =>
                                            handleInput('price', event.target.value)
                                        }
                                    />
                                </label>
                                <label>
                                    Devise
                                    <input
                                        type='text'
                                        value={form.currency}
                                        onChange={(event) =>
                                            handleInput(
                                                'currency',
                                                event.target.value.toUpperCase()
                                            )
                                        }
                                    />
                                </label>
                                <label>
                                    Status
                                    <select
                                        value={form.status}
                                        onChange={(event) =>
                                            handleInput('status', event.target.value)
                                        }
                                    >
                                        {STATUS_OPTIONS.map((item) => (
                                            <option
                                                key={item.value}
                                                value={item.value}
                                            >
                                                {item.label}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <label>
                                    Categorie
                                    <select
                                        value={form.category}
                                        onChange={(event) =>
                                            handleInput('category', event.target.value)
                                        }
                                    >
                                        {CATEGORY_OPTIONS.map((item) => (
                                            <option
                                                key={item.value}
                                                value={item.value}
                                            >
                                                {item.label}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                {!isEdit && (
                                    <p className='admin-products-help admin-products-span-2'>
                                        Astuce: passe le filtre sur "Cartes" puis ajoute
                                        tes cartes, le formulaire prendra cette categorie.
                                    </p>
                                )}
                                <label>
                                    Largeur (cm)
                                    <input
                                        type='number'
                                        step='0.1'
                                        min='0'
                                        value={form.width_cm}
                                        onChange={(event) =>
                                            handleInput(
                                                'width_cm',
                                                event.target.value
                                            )
                                        }
                                    />
                                </label>
                                <label>
                                    Hauteur (cm)
                                    <input
                                        type='number'
                                        step='0.1'
                                        min='0'
                                        value={form.height_cm}
                                        onChange={(event) =>
                                            handleInput(
                                                'height_cm',
                                                event.target.value
                                            )
                                        }
                                    />
                                </label>
                                <label className='admin-products-span-2'>
                                    Lien achat
                                    <input
                                        type='url'
                                        value={form.buy_link}
                                        onChange={(event) =>
                                            handleInput('buy_link', event.target.value)
                                        }
                                    />
                                </label>
                                <label>
                                    Mis a jour par
                                    <input
                                        type='text'
                                        value={form.updated_by}
                                        onChange={(event) =>
                                            handleInput(
                                                'updated_by',
                                                event.target.value
                                            )
                                        }
                                    />
                                </label>
                                <label className='admin-products-checkbox'>
                                    <input
                                        type='checkbox'
                                        checked={Boolean(form.is_sold)}
                                        onChange={(event) =>
                                            handleInput(
                                                'is_sold',
                                                event.target.checked
                                            )
                                        }
                                    />
                                    Vendu
                                </label>
                                <label className='admin-products-span-2'>
                                    Options (1 ligne = 1 option)
                                    <textarea
                                        rows={3}
                                        value={form.options_text}
                                        onChange={(event) =>
                                            handleInput(
                                                'options_text',
                                                event.target.value
                                            )
                                        }
                                        placeholder='Coloris bleu&#10;Version cadre'
                                    />
                                </label>

                                <div className='admin-products-span-2 admin-products-media'>
                                    <p className='eyebrow'>Media</p>
                                    <div className='admin-products-media-upload'>
                                        <input
                                            type='file'
                                            accept='image/*,video/*'
                                            multiple
                                            onChange={handleUploadFiles}
                                            onClick={(event) =>
                                                event.stopPropagation()
                                            }
                                            disabled={isUploading}
                                        />
                                        <button
                                            type='button'
                                            className='button button-secondary'
                                            onClick={handleAddManualMedia}
                                            disabled={!manualMediaUrl.trim()}
                                        >
                                            Ajouter URL
                                        </button>
                                        <input
                                            type='url'
                                            value={manualMediaUrl}
                                            placeholder='https://...'
                                            onKeyDown={handleManualMediaKeyDown}
                                            onChange={(event) =>
                                                setManualMediaUrl(
                                                    event.target.value
                                                )
                                            }
                                        />
                                    </div>
                                    {isUploading && (
                                        <p className='admin-alert'>
                                            {isOptimizing
                                                ? 'Optimisation media en cours...'
                                                : 'Upload en cours...'}
                                        </p>
                                    )}
                                    <div className='admin-products-media-list'>
                                        {form.media.map((url, index) => (
                                            <div
                                                key={`${url}-${index}`}
                                                className='admin-products-media-item'
                                            >
                                                <a
                                                    href={url}
                                                    target='_blank'
                                                    rel='noreferrer'
                                                >
                                                    {url}
                                                </a>
                                                <div className='admin-products-media-actions'>
                                                    <button
                                                        type='button'
                                                        className='button button-outline'
                                                        onClick={() =>
                                                            handleMoveMedia(
                                                                index,
                                                                -1
                                                            )
                                                        }
                                                        disabled={index === 0}
                                                    >
                                                        ↑
                                                    </button>
                                                    <button
                                                        type='button'
                                                        className='button button-outline'
                                                        onClick={() =>
                                                            handleMoveMedia(
                                                                index,
                                                                1
                                                            )
                                                        }
                                                        disabled={
                                                            index ===
                                                            form.media.length - 1
                                                        }
                                                    >
                                                        ↓
                                                    </button>
                                                    <button
                                                        type='button'
                                                        className='button button-outline'
                                                        onClick={() =>
                                                            handleRemoveMedia(
                                                                url
                                                            )
                                                        }
                                                    >
                                                        Retirer
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className='admin-products-actions admin-products-span-2'>
                                    <button
                                        type='submit'
                                        className='button'
                                        disabled={isSaving}
                                    >
                                        {isSaving
                                            ? 'Enregistrement...'
                                            : isEdit
                                            ? 'Mettre a jour'
                                            : 'Creer'}
                                    </button>
                                    <button
                                        type='button'
                                        className='button button-outline'
                                        onClick={resetForm}
                                    >
                                        {isEdit
                                            ? 'Passer en creation'
                                            : 'Reinitialiser'}
                                    </button>
                                </div>
                            </form>
                            <div className='admin-products-preview'>
                                <p className='eyebrow'>Previsualisation</p>
                                {form.category === 'tapis' ? (
                                    <div className='admin-products-preview-shell'>
                                        <ProductCard
                                            title={
                                                form.title?.trim() ||
                                                'Titre du produit'
                                            }
                                            price={String(
                                                Number(form.price) || 0
                                            ).replace('.', ',')}
                                            imageUrl={
                                                form.media.length > 1
                                                    ? form.media
                                                    : form.media[0] || ''
                                            }
                                            width={form.width_cm || '?'}
                                            height={form.height_cm || '?'}
                                            isSold={Boolean(form.is_sold)}
                                            linkCart={form.buy_link || ''}
                                        />
                                    </div>
                                ) : (
                                    <article className='product-card admin-card-preview'>
                                        <div className='product-media'>
                                            {previewMedia ? (
                                                previewMedia.type === 'video' ? (
                                                    <video
                                                        src={previewMedia.url}
                                                        autoPlay
                                                        loop
                                                        muted
                                                        playsInline
                                                        preload='metadata'
                                                    />
                                                ) : (
                                                    <img
                                                        src={previewMedia.url}
                                                        alt='Apercu carte'
                                                    />
                                                )
                                            ) : null}
                                            <div className='product-badge'>
                                                <span
                                                    className={`status ${
                                                        form.is_sold
                                                            ? 'sold'
                                                            : 'available'
                                                    }`}
                                                >
                                                    {form.is_sold
                                                        ? 'Vendu'
                                                        : 'Disponible'}
                                                </span>
                                            </div>
                                        </div>
                                        <div
                                            className={`product-thumbs ${
                                                previewMediaItems.length > 1
                                                    ? ''
                                                    : 'is-empty'
                                            }`}
                                        >
                                            {previewMediaItems.length > 1
                                                ? previewMediaItems.map(
                                                      (media, index) => (
                                                          <button
                                                              key={`${media.url}-${index}`}
                                                              type='button'
                                                              className={`thumb-button ${
                                                                  safePreviewIndex ===
                                                                  index
                                                                      ? 'active'
                                                                      : ''
                                                              }`}
                                                              onClick={() =>
                                                                  setPreviewMediaIndex(
                                                                      index
                                                                  )
                                                              }
                                                          >
                                                              {media.type ===
                                                              'video' ? (
                                                                  <div className='thumb-video'>
                                                                      <video
                                                                          src={
                                                                              media.url
                                                                          }
                                                                          muted
                                                                          playsInline
                                                                          preload='metadata'
                                                                      />
                                                                      <span className='thumb-play'>
                                                                          <PlayIcon
                                                                              size={
                                                                                  14
                                                                              }
                                                                          />
                                                                      </span>
                                                                  </div>
                                                              ) : (
                                                                  <img
                                                                      src={
                                                                          media.url
                                                                      }
                                                                      alt={
                                                                          media.label
                                                                      }
                                                                  />
                                                              )}
                                                          </button>
                                                      )
                                                  )
                                                : null}
                                        </div>
                                        <div className='product-body'>
                                            <div className='product-header'>
                                                <h3>
                                                    {form.title?.trim() ||
                                                        'Titre du produit'}
                                                </h3>
                                                {!form.is_sold ? (
                                                    <span className='product-price'>
                                                        {getPreviewPrice(
                                                            form.price,
                                                            form.currency
                                                        )}
                                                    </span>
                                                ) : null}
                                            </div>
                                            <p className='admin-products-preview-meta'>
                                                {form.status || 'draft'} ·{' '}
                                                {previewMediaItems.length} media
                                            </p>
                                        </div>
                                    </article>
                                )}
                            </div>
                        </article>

                        <article className='admin-card admin-products-list-card'>
                            <div className='admin-card-header'>
                                <div>
                                    <p className='eyebrow'>Catalogue</p>
                                    <h2>{items.length} produits</h2>
                                </div>
                            </div>

                            <div className='admin-products-filters'>
                                <div className='admin-products-toolbar'>
                                    <div className='admin-products-search-wrap'>
                                        <input
                                            type='search'
                                            value={search}
                                            placeholder='Rechercher titre/slug'
                                            onChange={(event) =>
                                                setSearch(event.target.value)
                                            }
                                        />
                                    </div>
                                    <div className='admin-products-toolbar-actions'>
                                        <button
                                            type='button'
                                            className='button button-outline'
                                            onClick={() =>
                                                setShowFilters((prev) => !prev)
                                            }
                                        >
                                            {showFilters
                                                ? 'Masquer filtres'
                                                : 'Filtres'}
                                        </button>
                                        <button
                                            type='button'
                                            className='button button-secondary'
                                            onClick={fetchProducts}
                                            disabled={status === 'loading'}
                                        >
                                            Rafraichir
                                        </button>
                                    </div>
                                </div>
                                <div
                                    className={`admin-products-filters-advanced ${
                                        showFilters ? 'is-open' : ''
                                    }`}
                                >
                                    <label>
                                        Categorie
                                        <select
                                            value={categoryFilter}
                                            onChange={(event) =>
                                                setCategoryFilter(
                                                    event.target.value
                                                )
                                            }
                                        >
                                            {CATEGORY_FILTERS.map((item) => (
                                                <option
                                                    key={item.value}
                                                    value={item.value}
                                                >
                                                    {item.label}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                    <label>
                                        Status
                                        <select
                                            value={statusFilter}
                                            onChange={(event) =>
                                                setStatusFilter(
                                                    event.target.value
                                                )
                                            }
                                        >
                                            <option value='all'>
                                                Tous les status
                                            </option>
                                            {STATUS_OPTIONS.map((item) => (
                                                <option
                                                    key={item.value}
                                                    value={item.value}
                                                >
                                                    {item.label}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                </div>
                            </div>

                            <div className='admin-products-list'>
                                {filteredItems.map((item) => (
                                    <article
                                        key={item.id}
                                        className='admin-products-item'
                                    >
                                        <div>
                                            <p className='admin-products-item-title'>
                                                {item.title}
                                            </p>
                                            <p className='admin-products-item-meta'>
                                                {(item.category || 'tapis').toUpperCase()} ·{' '}
                                                {item.slug} · {item.status} ·{' '}
                                                {item.is_sold ? 'Vendu' : 'En vente'} ·{' '}
                                                {(item.media || []).length} media
                                            </p>
                                        </div>
                                        <div className='admin-products-item-actions'>
                                            <button
                                                type='button'
                                                className='button button-outline'
                                                onClick={() => handleEdit(item)}
                                            >
                                                Editer
                                            </button>
                                            <button
                                                type='button'
                                                className='button button-outline'
                                                onClick={() =>
                                                    setDeleteTarget(item)
                                                }
                                            >
                                                Supprimer
                                            </button>
                                        </div>
                                    </article>
                                ))}
                                {!filteredItems.length && status !== 'loading' && (
                                    <p className='admin-alert'>
                                        Aucun produit trouve pour ce filtre.
                                    </p>
                                )}
                            </div>
                        </article>
                    </div>
                </section>
            </main>
            {deleteTarget && (
                <div className='admin-modal-backdrop' role='presentation'>
                    <div
                        className='admin-modal'
                        role='dialog'
                        aria-modal='true'
                        aria-labelledby='delete-product-title'
                    >
                        <h3 id='delete-product-title'>Supprimer ce produit ?</h3>
                        <p>
                            Cette action est definitive pour{' '}
                            <strong>{deleteTarget.title}</strong>.
                        </p>
                        <div className='admin-modal-actions'>
                            <button
                                type='button'
                                className='button button-outline'
                                onClick={() => setDeleteTarget(null)}
                                disabled={isDeleting}
                            >
                                Annuler
                            </button>
                            <button
                                type='button'
                                className='button'
                                onClick={() => handleDelete(deleteTarget.id)}
                                disabled={isDeleting}
                            >
                                {isDeleting ? 'Suppression...' : 'Confirmer'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminProductsPage;
